import { useEffect, useState, useCallback, useRef } from "react";
import RemisionDetailUI from "./RemisionDetailUI";
import { IDispatchWarehouse, IRemisionDetail, IRemisionProductSub, WarehousesStatus } from "src/types/global";
import { useLocation, useNavigate } from "react-router-dom";
import { authService } from "@services/authService";
import { remisionService } from "@services/RemisionService";
import { createHeader, formatDate, getCurrency } from "@utils/utils";
import { Stack, Tooltip, Typography } from "@mui/material";
import MyButton from "@components/atoms/MyButton/MyButton";
import { BoxArrowRight, ClockHistory, Trash } from "react-bootstrap-icons";
const RemisionDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [remision, setRemision] = useState<IRemisionDetail>({
    id: "- - -",
    name: "- - -",
    clientId: undefined,
    clientName: "- - -",
    limitDate: "- - -",
    creationTime: "- - -",
    requestProducts: [],
    returnProducts: [],
    total: 0,
    remainingAmount: 0,
    receiptType: 0,
    state: "Creado",
    createdByEmployeeName: "",
    lastModifiedByEmployeeName: ""
  });
  const [rows, setRows] = useState<IRemisionProductSub[]>([]);
  const [rows2, setRows2] = useState<IRemisionProductSub[]>([]);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPage2, setCurrentPage2] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Hubo problemas para consultar");
  const [showDialogDelete, setShowDialogDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [employeeCode, setEmployeeCode] = useState("");
  const [applicant, setApplicant] = useState("");
  const [product, setProduct] = useState<IRemisionProductSub | undefined>(undefined);

  // Lifecycle action state
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState<"approve" | "revoke" | "close" | "dispatch" | null>(null);
  const [actionEmployeeCode, setActionEmployeeCode] = useState("");
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [dispatchTarget, setDispatchTarget] = useState<IRemisionProductSub | undefined>(undefined);
  const [dispatchAmount, setDispatchAmount] = useState("");
  const [dispatchErrorMessage, setDispatchErrorMessage] = useState("");

  // Page-session warehouse cache: empty results are never cached, so an admin
  // creating a warehouse unblocks the operator without a page reload.
  const warehousesCacheRef = useRef<IDispatchWarehouse[] | null>(null);
  const [dispatchWarehouses, setDispatchWarehouses] = useState<IDispatchWarehouse[]>([]);
  const [dispatchWarehousesStatus, setDispatchWarehousesStatus] = useState<WarehousesStatus>("loading");
  const [dispatchWarehouseId, setDispatchWarehouseId] = useState("");
  const rowsPerPage = 10;
  const hasApprovePermission = authService.hasPermission("Remisiones.ApproveForDispatch");
  const canApprove = remision.state === "Pagado" && hasApprovePermission;
  const canRevoke = remision.state === "Aprobado" && hasApprovePermission;
  const canClose = remision.state === "Despachado" && hasApprovePermission;
  const getRemision = useCallback(async (id: number) => {
    try {
      setIsLoading(true);
      const res = await remisionService.get(id);
      const remisionDB = res.data;
      setPages(Math.ceil(remisionDB.requestProducts.length / rowsPerPage));
      setRemision(old => ({
        ...old,
        ...remisionDB,
        creationTime: formatDate(remisionDB.creationTime),
        limitDate: formatDate(remisionDB.limitDate),
        requestProducts: [...remisionDB.requestProducts.map(prod => ({
          ...prod,
          productRender: <Tooltip placement="top" title={prod.productReference} sx={{
            cursor: "pointer"
          }}>
                <Typography>{prod.productName}</Typography>
              </Tooltip>,
          employeeName: prod.createdByEmployeeName ? <Tooltip placement="top" title={`Solicitado por: ${prod.applicant}`} sx={{
            cursor: "pointer"
          }}>
                <Typography>{prod.createdByEmployeeName}</Typography>
              </Tooltip> : "N/A",
          price: getCurrency(prod.price),
          requestedTime: formatDate(prod.requestedTime),
          totalCurrency: getCurrency(prod.total)
        }))],
        returnProducts: [...remisionDB.returnProducts.map(prod => ({
          ...prod,
          productRender: <Tooltip placement="top" title={prod.productReference} sx={{
            cursor: "pointer"
          }}>
                <Typography>{prod.productName}</Typography>
              </Tooltip>,
          employeeName: prod.createdByEmployeeName ? <Tooltip placement="top" title={`Solicitado por: ${prod.applicant}`} sx={{
            cursor: "pointer"
          }}>
                <Typography>{prod.createdByEmployeeName}</Typography>
              </Tooltip> : "N/A",
          price: getCurrency(prod.price),
          requestedTime: formatDate(prod.requestedTime),
          totalCurrency: getCurrency(-prod.total)
        }))]
      }));
    } catch {
      setErrorMessage("Hubo problemas para consultar");
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);
  useEffect(() => {
    const id = location.pathname.split("/").reverse()[0];
    const routeState: IRemisionDetail = location.state;
    if (id === routeState?.id) {
      setRemision(old => ({
        ...old,
        id,
        clientName: routeState.clientName,
        total: routeState.total
      }));
    }
    getRemision(Number(id));
  }, [getRemision, location]);
  const handleOpenDispatch = useCallback(async (reqProduct: IRemisionProductSub) => {
    if (!reqProduct.remisionDetailId) return;
    setDispatchTarget(reqProduct);
    setDispatchAmount("");
    setDispatchWarehouseId("");
    setDispatchErrorMessage("");
    setActionType("dispatch");
    // Entry point behavior unchanged: the dialog opens immediately, the
    // warehouse list resolves after (cache hit or async lookup).
    setShowActionDialog(true);
    const cached = warehousesCacheRef.current;
    if (cached) {
      setDispatchWarehouses(cached);
      setDispatchWarehousesStatus(cached.length > 0 ? "ready" : "empty");
      return;
    }
    setDispatchWarehousesStatus("loading");
    try {
      const res = await remisionService.getDispatchWarehouses();
      const list = res.data;
      setDispatchWarehouses(list);
      if (list.length > 0) {
        // Never cache empty results, so an admin creating a warehouse
        // unblocks the operator without a page reload.
        warehousesCacheRef.current = list;
        setDispatchWarehousesStatus("ready");
      } else {
        setDispatchWarehousesStatus("empty");
      }
    } catch {
      setDispatchWarehouses([]);
      setDispatchWarehousesStatus("error");
    }
  }, []);
  useEffect(() => {
    setRows([...remision.requestProducts.slice(rowsPerPage * (currentPage - 1), rowsPerPage * currentPage).map(reqProduct => ({
      ...reqProduct,
      dispatchedRender: <Stack direction="row" justifyContent="center" alignItems="center">
              <Typography>{`${reqProduct.dispatchedQuantity ?? 0} / ${reqProduct.quantity}`}</Typography>
            </Stack>,
      actions: <Stack direction={"row"} justifyContent={"center"} spacing={1}>
              {remision.state === "Aprobado" && <Tooltip title="Despachar" placement="right">
                  <MyButton aria-label="Despachar" onClick={() => handleOpenDispatch(reqProduct)} disabled={isDeleting || reqProduct.quantity - (reqProduct.dispatchedQuantity ?? 0) <= 0}>
                    <BoxArrowRight size={22} />
                  </MyButton>
                </Tooltip>}
              <Tooltip title="Ver despachos" placement="right">
                <MyButton aria-label="Ver despachos" onClick={() => navigate(`/remisiones/${remision.id}/despachos/${reqProduct.remisionDetailId}`, {
            state: {
              productName: reqProduct.productName
            }
          })}>
                  <ClockHistory size={22} />
                </MyButton>
              </Tooltip>
              {authService.isAdmin() && remision.state === "Creado" && <Tooltip title="Eliminar" placement="right">
                  <MyButton color="error" onClick={() => {
            setProduct(reqProduct);
            setShowDialogDelete(true);
          }} disabled={isDeleting}>
                    <Trash size={22} />
                  </MyButton>
                </Tooltip>}
            </Stack>
    }))]);
  }, [remision, currentPage, isDeleting, handleOpenDispatch, navigate]);
  useEffect(() => {
    setRows2([...remision.returnProducts.slice(rowsPerPage * (currentPage2 - 1), rowsPerPage * currentPage2).map(retProduct => ({
      ...retProduct,
      actions: <Stack direction={"row"} justifyContent={"center"} spacing={1}>
              {remision.state === "Creado" && <Tooltip title="Eliminar" placement="right">
                  <MyButton color="error" onClick={() => {
            setProduct(retProduct);
            setShowDialogDelete(true);
          }} disabled={isDeleting}>
                    <Trash size={22} />
                  </MyButton>
                </Tooltip>}
            </Stack>
    }))]);
  }, [remision, currentPage2, isDeleting]);
  const handleDelete = useCallback(async () => {
    try {
      setIsDeleting(true);
      const remisionDetailId = product?.remisionDetailId;
      if (!remisionDetailId || !employeeCode) throw new Error();
      const remisionIdNumber = Number(remision.id);
      const payload = {
        remisionId: remisionIdNumber,
        remisionDetailId,
        operation: 2,
        employeeCode,
        applicant
      };
      await remisionService.put(payload);
      await getRemision(remisionIdNumber);
      setEmployeeCode("");
      setApplicant("");
    } catch {
      setErrorMessage(`Lo sentimos, no se pudo eliminar ${product?.productName}`);
      setShowError(true);
    } finally {
      setIsDeleting(false);
      setShowDialogDelete(false);
    }
  }, [product, employeeCode, applicant, remision.id, getRemision]);
  const handleConfirmAction = useCallback(async () => {
    if (!actionType || !actionEmployeeCode) return;

    // ADR-6: DispatchDialog's confirm re-validates before calling onConfirm, but
    // ConfirmDialog's Enter handler ignores `disabled`, so this is re-checked here too.
    if (actionType === "dispatch") {
      const remaining = dispatchTarget ? dispatchTarget.quantity - (dispatchTarget.dispatchedQuantity ?? 0) : 0;
      const amountValue = Number(dispatchAmount);
      const isValidAmount = dispatchAmount.trim() !== "" && !Number.isNaN(amountValue) && amountValue > 0 && amountValue <= remaining;
      if (!isValidAmount) return;
      if (!dispatchWarehouseId || dispatchWarehousesStatus !== "ready") return;
    }
    try {
      setIsActionLoading(true);
      const remisionIdNumber = Number(remision.id);
      if (actionType === "approve") {
        await remisionService.approve({
          remisionId: remisionIdNumber,
          employeeCode: actionEmployeeCode
        });
      } else if (actionType === "revoke") {
        await remisionService.revokeApproval({
          remisionId: remisionIdNumber,
          employeeCode: actionEmployeeCode
        });
      } else if (actionType === "close") {
        await remisionService.closeRemision({
          remisionId: remisionIdNumber,
          employeeCode: actionEmployeeCode
        });
      } else if (actionType === "dispatch" && dispatchTarget?.remisionDetailId) {
        await remisionService.markDetailDispatched({
          remisionId: remisionIdNumber,
          detailId: dispatchTarget.remisionDetailId,
          quantity: Number(dispatchAmount),
          employeeCode: actionEmployeeCode,
          warehouseId: Number(dispatchWarehouseId)
        });
      }
      await getRemision(remisionIdNumber);
      setShowActionDialog(false);
      setActionEmployeeCode("");
      setActionType(null);
      setDispatchTarget(undefined);
      setDispatchAmount("");
      setDispatchWarehouseId("");
      setDispatchErrorMessage("");
    } catch {
      if (actionType === "dispatch") {
        setDispatchErrorMessage("Lo sentimos, no se pudo completar el despacho");
      } else {
        setErrorMessage("Lo sentimos, no se pudo completar la acción");
        setShowError(true);
      }
    } finally {
      setIsActionLoading(false);
    }
  }, [actionType, actionEmployeeCode, remision.id, getRemision, dispatchTarget, dispatchAmount, dispatchWarehouseId, dispatchWarehousesStatus]);
  const handleCloseActionDialog = () => {
    setShowActionDialog(false);
    setActionEmployeeCode("");
    setActionType(null);
    setDispatchTarget(undefined);
    setDispatchAmount("");
    setDispatchWarehouseId("");
    setDispatchErrorMessage("");
  };

  // The Acciones column for request lines is always shown: every line carries
  // the dispatch-history drill-down ("Ver despachos"), alongside the dispatch
  // toggle (Aprobado only) and delete (admin + Creado only).
  //   returns → delete (admin + Creado)
  const isAdmin = authService.isAdmin();
  const requestActionsVisible = true;
  const returnActionsVisible = isAdmin && remision.state === "Creado";
  const headers = [createHeader("Fecha", "requestedTime"), createHeader("Producto", "productRender"), createHeader("Cantidad", "quantity"), createHeader("Precio unidad", "price"), createHeader("Total", "totalCurrency"), createHeader("Autor", "employeeName"), createHeader("Despachado", "dispatchedRender"), createHeader("Acciones", "actions")].filter(item => {
    if (item.id === "actions") return requestActionsVisible;
    if (item.id === "dispatchedRender") return remision.state !== "Aprobado";
    return true;
  });
  const headers2 = [createHeader("Fecha", "requestedTime"), createHeader("Producto", "productRender"), createHeader("Cantidad", "quantity"), createHeader("Precio unidad", "price"), createHeader("Total", "totalCurrency"), createHeader("Autor", "employeeName"), createHeader("Acciones", "actions")].filter(item => item.id !== "actions" || returnActionsVisible);
  return <RemisionDetailUI id={remision.id} name={remision.name} type={remision.receiptType} clientId={remision.clientId} clientName={remision.clientName} creationTime={remision.creationTime} limitDate={remision.limitDate} createdByEmployeeName={remision.createdByEmployeeName} lastModifiedByEmployeeName={remision.lastModifiedByEmployeeName} total={remision.total} total2={getCurrency(-remision.returnProducts.reduce((sum, prod) => sum + prod.total, 0))} headers={headers} rows={rows} rows2={rows2} headers2={headers2} pages={pages} onChangePage={setCurrentPage} onChangePage2={setCurrentPage2} isLoading={isLoading} errorMessage={errorMessage} showError={showError} closeError={() => setShowError(false)} remision={remision} setRemision={setRemision} showDialog={showDialogDelete} handleConfirmDialog={handleDelete} handleCloseDialog={() => {
    setShowDialogDelete(false);
    setEmployeeCode("");
    setApplicant("");
  }} employeeCode={employeeCode} setEmployeeCode={setEmployeeCode} applicant={applicant} setApplicant={setApplicant} canApprove={canApprove} canRevoke={canRevoke} canClose={canClose} onApproveClick={() => {
    setActionType("approve");
    setShowActionDialog(true);
  }} onRevokeClick={() => {
    setActionType("revoke");
    setShowActionDialog(true);
  }} onCloseClick={() => {
    setActionType("close");
    setShowActionDialog(true);
  }} showActionDialog={showActionDialog} handleConfirmAction={handleConfirmAction} handleCloseActionDialog={handleCloseActionDialog} actionType={actionType} actionEmployeeCode={actionEmployeeCode} setActionEmployeeCode={setActionEmployeeCode} isActionLoading={isActionLoading} dispatchTarget={dispatchTarget} dispatchAmount={dispatchAmount} setDispatchAmount={setDispatchAmount} dispatchErrorMessage={dispatchErrorMessage} dispatchWarehouses={dispatchWarehouses} dispatchWarehousesStatus={dispatchWarehousesStatus} dispatchWarehouseId={dispatchWarehouseId} setDispatchWarehouseId={setDispatchWarehouseId} />;
};
export default RemisionDetail;